<script setup lang="ts">
import { onMounted, onUnmounted, reactive } from 'vue';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { CircleCheck, TriangleAlert } from 'lucide-vue-next';
import { Button } from '../../ui/button';
import { startSessionAction } from '@/actions/run/StartSessionAction.ts';
import { eventEmitter, type ShowTrialInputWarningPayload } from '@/lib/eventEmitter.ts';

interface ComponentState {
    inputSourceName: string | null;
    screenCaptureData: string | undefined;
    shownImage: number;
    isOpen: boolean;
}

const state: ComponentState = reactive({
    inputSourceName: null,
    screenCaptureData: undefined,
    shownImage: 0,
    isOpen: false,
});

const showImage = (shownImage: number) => {
    state.shownImage = shownImage;
};

const ignoreWarning = () => {
    state.isOpen = false;

    void startSessionAction(false);
};

const openDialog = (payload: ShowTrialInputWarningPayload) => {
    state.inputSourceName = payload.inputSourceName;
    state.screenCaptureData = payload.screenCaptureData;
    state.shownImage = 0;
    state.isOpen = true;
};

onMounted(() => {
    eventEmitter.addListener('showTrialInputWarning', openDialog);
});

onUnmounted(() => {
    eventEmitter.removeListener('showTrialInputWarning', openDialog);
});
</script>

<template>
    <Dialog v-model:open="state.isOpen">
        <DialogContent class="max-h-[90dvh] grid-rows-[auto_minmax(0,1fr)_auto] p-0">
            <DialogHeader class="p-6 pb-0">
                <DialogTitle>Trial input warning</DialogTitle>
            </DialogHeader>
            <div class="gap-4 overflow-y-auto px-6 py-4">
                <div class="flex flex-col justify-between">
                    <div
                        v-if="state.shownImage !== 0"
                        class="absolute left-0 top-0 z-50 flex h-full w-full items-center justify-center p-4 backdrop-blur hover:cursor-zoom-out"
                        @click="showImage(0)"
                    >
                        <img
                            v-if="state.shownImage === 1"
                            class="rounded hover:cursor-zoom-out"
                            src="@/assets/img/example-input-1.png"
                            alt="Example usage 1"
                        />
                        <img
                            v-else-if="state.shownImage === 2"
                            class="rounded hover:cursor-zoom-out"
                            src="@/assets/img/example-input-2.png"
                            alt="Example usage 2"
                        />
                        <img
                            v-else-if="state.shownImage === 3 && state.screenCaptureData"
                            class="rounded hover:cursor-zoom-out"
                            :src="state.screenCaptureData"
                            alt="Example usage 1"
                        />
                    </div>

                    <div class="gap-0 text-sm">
                        <p>
                            We were unable to detect a LeetCode.com problem in your input<span
                                v-if="state.inputSourceName"
                            >
                                ("{{ state.inputSourceName }}")</span
                            >.
                        </p>

                        <div
                            class="relative my-3 rounded border border-gray-600 bg-gray-900 px-4 py-2"
                        >
                            <p class="mb-1 flex items-center text-green-600">
                                <CircleCheck class="me-2 h-4 w-4" />
                                Examples of valid input:
                            </p>
                            <div class="mb-4 flex items-center gap-x-2">
                                <div class="w-1/2">
                                    <img
                                        @click="showImage(1)"
                                        class="me-2 rounded hover:cursor-zoom-in"
                                        src="@/assets/img/example-input-1.png"
                                        alt="Example usage 1"
                                    />
                                </div>
                                <div class="w-1/2">
                                    <img
                                        @click="showImage(2)"
                                        class="rounded hover:cursor-zoom-in"
                                        src="@/assets/img/example-input-2.png"
                                        alt="Example usage 2"
                                    />
                                </div>
                            </div>

                            <div v-if="state.screenCaptureData">
                                <p class="mb-1 flex items-center text-red-600">
                                    <TriangleAlert class="me-2 h-4 w-4" />
                                    Your input:
                                </p>
                                <img
                                    class="mb-4 me-2 w-1/2 rounded hover:cursor-zoom-in"
                                    :src="state.screenCaptureData"
                                    alt="User input"
                                    @click="showImage(3)"
                                />
                            </div>
                        </div>

                        <p class="mb-2">
                            The trial mode of Leetcode Wizard only works on
                            <a
                                href="https://leetcode.com/problemset/"
                                target="_blank"
                                class="underline"
                                >LeetCode.com</a
                            >. Our Pro version works on all websites.
                        </p>

                        <hr class="my-2" />

                        <p>
                            If this is a mistake please press the button below to continue. This
                            warning is only shown during trial mode.
                        </p>
                    </div>
                </div>
            </div>
            <DialogFooter class="p-6 pt-0">
                <Button type="button" @click="ignoreWarning">
                    <span>Ignore warning</span>
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<style scoped>
.hover\:cursor-zoom-in:hover {
    cursor: zoom-in !important;
}

.hover\:cursor-zoom-out:hover {
    cursor: zoom-out !important;
}
</style>
